/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import {
  InvalidateQueryFilters,
  QueryClient,
  useQuery,
  UseQueryResult,
  useSuspenseQuery,
  UseSuspenseQueryResult,
} from "@tanstack/react-query";
import { GramError } from "../models/errors/gramerror.js";
import {
  ConnectionError,
  InvalidRequestError,
  RequestAbortedError,
  RequestTimeoutError,
  UnexpectedClientError,
} from "../models/errors/httpclienterrors.js";
import { ResponseValidationError } from "../models/errors/responsevalidationerror.js";
import { SDKValidationError } from "../models/errors/sdkvalidationerror.js";
import { ServiceError } from "../models/errors/serviceerror.js";
import {
  ListToolVariationGroupsRequest,
  ListToolVariationGroupsSecurity,
} from "../models/operations/listtoolvariationgroups.js";
import { useGramContext } from "./_context.js";
import {
  QueryHookOptions,
  SuspenseQueryHookOptions,
  TupleToPrefixes,
} from "./_types.js";
import {
  buildToolVariationGroupsQuery,
  prefetchToolVariationGroups,
  queryKeyToolVariationGroups,
  ToolVariationGroupsQueryData,
} from "./toolVariationGroups.core.js";
export {
  buildToolVariationGroupsQuery,
  prefetchToolVariationGroups,
  queryKeyToolVariationGroups,
  type ToolVariationGroupsQueryData,
};

export type ToolVariationGroupsQueryError =
  | ServiceError
  | GramError
  | ResponseValidationError
  | ConnectionError
  | RequestAbortedError
  | RequestTimeoutError
  | InvalidRequestError
  | UnexpectedClientError
  | SDKValidationError;

/**
 * listGroups variations
 *
 * @remarks
 * List the tool variation groups visible to the project. In v1 this returns the project-default group when it exists, or an empty list otherwise.
 */
export function useToolVariationGroups(
  request?: ListToolVariationGroupsRequest | undefined,
  security?: ListToolVariationGroupsSecurity | undefined,
  options?: QueryHookOptions<
    ToolVariationGroupsQueryData,
    ToolVariationGroupsQueryError
  >,
): UseQueryResult<ToolVariationGroupsQueryData, ToolVariationGroupsQueryError> {
  const client = useGramContext();
  return useQuery({
    ...buildToolVariationGroupsQuery(
      client,
      request,
      security,
      options,
    ),
    ...options,
  });
}

/**
 * listGroups variations
 *
 * @remarks
 * List the tool variation groups visible to the project. In v1 this returns the project-default group when it exists, or an empty list otherwise.
 */
export function useToolVariationGroupsSuspense(
  request?: ListToolVariationGroupsRequest | undefined,
  security?: ListToolVariationGroupsSecurity | undefined,
  options?: SuspenseQueryHookOptions<
    ToolVariationGroupsQueryData,
    ToolVariationGroupsQueryError
  >,
): UseSuspenseQueryResult<
  ToolVariationGroupsQueryData,
  ToolVariationGroupsQueryError
> {
  const client = useGramContext();
  return useSuspenseQuery({
    ...buildToolVariationGroupsQuery(
      client,
      request,
      security,
      options,
    ),
    ...options,
  });
}

export function setToolVariationGroupsData(
  client: QueryClient,
  queryKeyBase: [
    parameters: {
      gramSession?: string | undefined;
      gramKey?: string | undefined;
      gramProject?: string | undefined;
    },
  ],
  data: ToolVariationGroupsQueryData,
): ToolVariationGroupsQueryData | undefined {
  const key = queryKeyToolVariationGroups(...queryKeyBase);

  return client.setQueryData<ToolVariationGroupsQueryData>(key, data);
}

export function invalidateToolVariationGroups(
  client: QueryClient,
  queryKeyBase: TupleToPrefixes<
    [parameters: {
      gramSession?: string | undefined;
      gramKey?: string | undefined;
      gramProject?: string | undefined;
    }]
  >,
  filters?: Omit<InvalidateQueryFilters, "queryKey" | "predicate" | "exact">,
): Promise<void> {
  return client.invalidateQueries({
    ...filters,
    queryKey: ["@gram/client", "variations", "listGroups", ...queryKeyBase],
  });
}

export function invalidateAllToolVariationGroups(
  client: QueryClient,
  filters?: Omit<InvalidateQueryFilters, "queryKey" | "predicate" | "exact">,
): Promise<void> {
  return client.invalidateQueries({
    ...filters,
    queryKey: ["@gram/client", "variations", "listGroups"],
  });
}
